import {toAbsoluteUrl} from '_metronic/helpers'
import {FC, useRef, useState, useEffect} from 'react'
import Searchbar from '../components/Searchbar'
import {ControlDataType} from '../components/Controls'

type Props = {
  controls: ControlDataType[]
}

type Group = {
  id: string
  name: string
  list: {title: string; description: string}[]
  filteredList: {title: string; description: string}[]
}

const ControlsTab: FC<Props> = ({controls}) => {
  const [controlData, setControlData] = useState<Group[]>(() => {
    const categorizedControlsObj: Record<string, ControlDataType[]> = controls?.reduce(
      (acc, control) => {
        const cateogry = control.category || 'Custom'
        if (!acc[cateogry]) {
          acc[cateogry] = []
        }
        acc[cateogry].push(control)
        return acc
      },
      {} as Record<string, ControlDataType[]>
    )

    const categorizedControlsArr = Object.entries(categorizedControlsObj)
      .map(([category, controls]) => ({
        id: category.toLowerCase(),
        name: category,
        list: controls,
        filteredList: controls,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))

    return categorizedControlsArr
  })

  const [searchTerm, setSearchTerm] = useState<string>('')
  const [isNoSearchFound, setIsNoSearchFound] = useState(false)

  useEffect(() => {
    if (controls?.length > 0) {
      const categorizedControlsObj: Record<string, ControlDataType[]> = controls?.reduce(
        (acc, control) => {
          const cateogry = control.category || 'Custom'
          if (!acc[cateogry]) {
            acc[cateogry] = []
          }
          acc[cateogry].push(control)
          return acc
        },
        {} as Record<string, ControlDataType[]>
      )

      const categorizedControlsArr = Object.entries(categorizedControlsObj)
        .map(([category, controls]) => ({
          id: category.toLowerCase(),
          name: category,
          list: controls,
          filteredList: controls,
        }))
        .sort((a, b) => a.name.localeCompare(b.name))

      setControlData(categorizedControlsArr)
    }
  }, [controls])

  useEffect(() => {
    const updatedData = controlData.map((group) => ({
      ...group,
      filteredList: group.list.filter((item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    }))
    setControlData(updatedData)

    setIsNoSearchFound(updatedData.filter((group) => group.filteredList.length > 0).length === 0)
  }, [searchTerm])

  return (
    <div className='w-100 d-flex flex-column flex-lg-row' style={{gap: '32px'}}>
      <div className='w-100 w-lg-25 d-flex flex-column' style={{gap: '12px', flexShrink: '0'}}>
        <div className='d-flex justify-content-between align-items-center'>
          <h3
            className='d-lg-none flex-fill text-custom-primary-gray mb-0'
            style={{fontSize: '20px', fontWeight: '600'}}
          >
            Controls
          </h3>

          <div className='flex-lg-fill'>
            <Searchbar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          </div>
        </div>

        {!isNoSearchFound && (
          <div
            className='d-none d-lg-flex flex-column'
            style={{
              gap: '4px',
              maxHeight:'38vh',
              overflowY:'auto',
              paddingRight:'4px'
            }}
          >
            {controlData.map(
              (group, idx) =>
                group.filteredList.length > 0 && (
                  <div
                    key={group.id}
                    className={`cursor-pointer text-custom-primary-gray`}
                    style={{
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '300',
                      padding: '6px 8px',
                    }}
                    onClick={() => {
                      const element = document.getElementById(group.id)
                      element?.scrollIntoView({behavior: 'smooth'})
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                      e.currentTarget.style.backgroundColor = '#F3F4F6'
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    {group.name}
                  </div>
                )
            )}
          </div>
        )}
      </div>

      <div
        className='flex-fill d-flex flex-column'
        style={{
          gap: '40px',
        }}
      >
        {!isNoSearchFound ? (
          controlData.map(
            (group, index) =>
              group.filteredList.length > 0 && (
                <div key={group.id} id={group.id} className=''>
                  <h3
                    className='fw-medium text-custom-primary-gray'
                    style={{fontSize: '18px', marginBottom: '24px'}}
                  >
                    {group.name}
                  </h3>

                  <div className='d-flex flex-column' style={{gap: '20px'}}>
                    {group.filteredList.map((item, index) => (
                      <div
                        key={index}
                        className='d-flex align-items-start bg-white'
                        style={{
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          gap: '16px',
                          padding: '24px',
                        }}
                      >
                        <img
                          alt='circle-check-icon'
                          src={toAbsoluteUrl('/media/icon/circle-check-gray-900.svg')}
                          className=''
                          style={{height: '20px', width: '20px'}}
                        />

                        <div className='d-flex flex-column' style={{gap: '8px'}}>
                          <h4 className='fw-medium text-custom-primary-gray mb-0' style={{fontSize: '16px'}}>
                            {item.title}
                          </h4>
                          <p className='text-custom-secondary-gray mb-0' style={{fontSize: '14px'}}>
                            {item.description.replace(/<\/?[^>]+(>|$)/g, '')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
          )
        ) : (
          <div
            className='w-100 h-100 d-flex align-items-center justify-content-center'
            style={{border: '1px solid #D1D5DB', borderRadius: '8px', paddingBlock: '24px'}}
          >
            <span className='text-custom-primary-gray fw-medium ' style={{fontSize: '16px'}}>
              {controlData.length > 0 ? 'No matching controls found' : 'No controls added'}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default ControlsTab
